import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Search, ArrowLeft } from 'lucide-react';
import { toast } from 'react-hot-toast';
import CarrierFinder from './CarrierFinder';
import { OrgaConfigurationRepository } from '@/services/firebase/orga-configuration-repository';
import SeoTags from '@/components/shared/SeoTags';
import { useTheme } from '@/shared/contexts/ThemeContext';
import type { ShippingConfiguration, ShippingMethodConfig } from '@/models/orgaConfiguration';

const ShippingMethodsSearch: React.FC = () => {
  const { idorga } = useParams<{ idorga: string }>();
  const organizationId = idorga;
  const [shippingMethods, setShippingMethods] = useState<ShippingMethodConfig[]>([]);
  const { theme } = useTheme();

  const loadMethods = useCallback(async () => {
    if (!organizationId) return;
    try {
      const config = await OrgaConfigurationRepository.getByOrganizationId(organizationId);
      setShippingMethods(config?.shippingConfig?.methods ?? []);
    } catch (error) {
      console.error('Erreur lors du chargement des méthodes:', error);
    }
  }, [organizationId]);

  useEffect(() => {
    loadMethods();
  }, [loadMethods]);

  const alreadyAddedSendcloudIds = useMemo(
    () =>
      shippingMethods
        .filter(m => m.provider === 'sendcloud' && m.sendcloudMethodId != null)
        .map(m => m.sendcloudMethodId as number),
    [shippingMethods],
  );

  const handleAddMethod = async (method: ShippingMethodConfig) => {
    if (!organizationId) return;
    try {
      const config = await OrgaConfigurationRepository.getByOrganizationId(organizationId);
      const current: ShippingConfiguration = {
        methods: [],
        ...config?.shippingConfig,
      };
      const next: ShippingConfiguration = {
        ...current,
        methods: [...(current.methods ?? []), method],
      };
      await OrgaConfigurationRepository.update(organizationId, { shippingConfig: next });
      setShippingMethods(next.methods);
      toast.success('Méthode de livraison ajoutée');
    } catch (error) {
      console.error('Erreur lors de l’ajout de la méthode:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  if (!organizationId) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Espace de vente non trouvé</p>
      </div>
    );
  }

  return (
    <div className="w-full mt-20">
      <SeoTags 
        title={`Rechercher des méthodes de livraison | CashLive`}
        description={`Explorez et ajoutez des méthodes de livraison.`}
        url={typeof window !== 'undefined' ? window.location.href : ''}
        noIndex={true}
      />
      {/* Header avec navigation */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Search className="w-6 h-6" style={{ color: theme.colors.success || '#10B981' }} />
            <div>
              <h1 className="text-2xl font-bold" style={{ color: theme.colors.foreground || '#111827' }}>
                Rechercher des méthodes de livraison
              </h1>
              <p className="mt-1" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                Explorez et ajoutez de nouvelles méthodes de livraison disponibles
              </p>
            </div>
          </div>
          <Link
            to={`/orga/${organizationId}/admin/configuration#shipping`}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: theme.colors.primary || '#4B5563',
              color: theme.colors.background || '#FFFFFF',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.primaryDark || '#374151';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.primary || '#4B5563';
            }}
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour aux méthodes actives</span>
          </Link>
        </div>
      </div>

      {/* Carrier Finder */}
      <div className="rounded-lg shadow p-6" style={{ backgroundColor: theme.colors.background || '#FFFFFF' }}>
        <CarrierFinder
          alreadyAddedIds={alreadyAddedSendcloudIds}
          onAddMethod={handleAddMethod}
        />
      </div>
    </div>
  );
};

export default ShippingMethodsSearch;
